/**
 * English source strings for every UI surface.
 *
 * All other locales in ./translations/<locale>.ts export the same keys.
 * LanguageContext imports them, keyed by short locale code, and falls
 * back to English when a key is missing from the active locale (so a
 * partial translation degrades to English on that single string rather
 * than crashing the render).
 */
export const en = {
// Navigation
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.products': 'Products',
    'nav.capabilities': 'Capabilities',
    'nav.quality': 'Quality & Compliance',
    'nav.news': 'News',
    'nav.contact': 'Contact',
    'header.phone': 'Phone',
    'header.email': 'Email',
    'header.rfq_cart': 'Cart',
    'button.request_quote': 'Request Quote',
    'search.placeholder': 'Search by CAS No. or Product Name',
    'language.change': 'Change Language',

    // Hero Section
    'hero.badge': 'ISO 9001:2015 Certified Excellence',
    'hero.title.leading': 'Leading',
    'hero.title.pharmaceutical': 'Pharmaceutical',
    'hero.title.innovation': 'Innovation',
    'hero.description': 'Longshine Boi-Tech delivers premium APIs, pharmaceutical intermediates, and veterinary compounds to global markets with ISO-certified quality assurance from our ISO-certified facilities in India.',
    'hero.button.explore': 'Explore Products',
    'hero.button.quote': 'Request Quote',
    'hero.stats.countries': 'Countries Served',
    'hero.stats.products': 'Active Products',
    'hero.stats.years': 'Years of Excellence',

    // Product Categories
    'categories.title': 'Premium Pharmaceutical Solutions',
    'categories.subtitle': 'Discover our comprehensive range of APIs, intermediates, and specialized compounds manufactured to the highest international standards.',
    'categories.search.placeholder': 'Search by CAS Number, Product Name, or Category...',
    'categories.human_apis.title': 'Human APIs',
    'categories.human_apis.description': 'Pharmaceutical active ingredients for human therapeutics with full regulatory documentation.',
    'categories.intermediates.title': 'Intermediates',
    'categories.intermediates.description': 'High-quality pharmaceutical intermediates and building blocks for drug synthesis.',
    'categories.veterinary.title': 'Veterinary APIs',
    'categories.veterinary.description': 'Specialized compounds for animal health and veterinary pharmaceutical applications.',
    'categories.products_count': '+ Products',

    // Featured Products
    'featured.title': 'Featured Products',
    'featured.subtitle': 'Explore our most popular pharmaceutical compounds trusted by global manufacturers.',
    'featured.view_all': 'View All Products',

    // Company Features
    'features.title': 'Why Choose Longshine Boi-Tech',
    'features.subtitle': 'Decades of pharmaceutical know-how plus current-generation process technology — delivering APIs and intermediates that hold up against any global standard.',
    'features.iso.title': 'ISO 9001:2015 Certified',
    'features.iso.description': 'Internationally recognized quality management system ensuring consistent excellence in all our processes and products.',
    'features.global.title': 'Global Supply Chain',
    'features.global.description': 'Reliable worldwide distribution network serving pharmaceutical companies across 50+ countries with timely delivery.',
    'features.compliance.title': 'Regulatory Compliance',
    'features.compliance.description': 'Full compliance with FDA, EMA, and other international regulatory standards for pharmaceutical manufacturing.',
    'features.custom.title': 'Custom Manufacturing',
    'features.custom.description': 'Flexible CMO/CDMO services for custom synthesis, scale-up production, and specialized pharmaceutical compounds.',
    'features.team.title': 'Expert Team',
    'features.team.description': 'Experienced R&D professionals and regulatory experts dedicated to delivering innovative pharmaceutical solutions.',
    'features.facilities.title': 'Advanced Facilities',
    'features.facilities.description': 'Modern manufacturing facilities with current-generation process equipment and validated quality control systems.',

    // CTA Section
    'cta.title': 'Ready to Partner with Us?',
    'cta.subtitle': 'Join hundreds of global pharmaceutical companies who trust Longshine Boi-Tech for their API and intermediate supply needs.',
    'cta.quote_now': 'Request Quote Now',
    'cta.download_catalog': 'Download Catalog',

    // Product Card
    'product.cas': 'CAS',
    'product.moq': 'MOQ',
    'product.bulk_discount': 'Bulk discounts available',
    'product.details': 'Details',
    'product.sds': 'SDS',
    'product.add_rfq': 'Add to cart',
    'product.download_started': 'Download Started',
    'product.sds_downloading': 'Safety Data Sheet for {product} is downloading',
    'product.download_error': 'Download Error',
    'product.sds_error': 'Could not download SDS for {product}. Please contact support.',
    'product.added_rfq': 'Added to cart',
    'product.added_cart': '{product} has been added to your cart',
    'product.view_cart': 'View Cart',

    // About Page
    'about.hero.title': 'About Longshine Boi-Tech',
    'about.hero.description': 'Since 2009, we have been at the forefront of pharmaceutical innovation, delivering premium APIs and intermediates that meet the highest global standards while building trusted partnerships with manufacturers worldwide.',
    'about.hero.years': '15+ Years of Excellence',
    'about.hero.global': 'Global Reach',
    'about.story.title': 'Our Story',
    'about.story.p1': 'Longshine Boi-Tech was founded in 2009 with a vision to bridge the gap between innovative pharmaceutical research and reliable global supply. Starting as a small research facility in Pune, Maharashtra, we have grown into a leading manufacturer of pharmaceutical active ingredients.',
    'about.story.p2': 'Our commitment to quality and regulatory compliance has enabled us to build long-term partnerships with pharmaceutical companies across six continents. Every product we manufacture undergoes rigorous testing and quality assurance to ensure it meets international pharmaceutical standards.',
    'about.story.p3': 'Today, Longshine Boi-Tech is recognized as a trusted supplier of premium APIs, intermediates, and veterinary compounds, with ISO 9001:2015 certification and multiple regulatory approvals including FDA DMFs and European CEPs.',
    'about.journey.title': 'Our Journey',
    'about.journey.subtitle': 'Key milestones that shaped our growth',
    'about.leadership.title': 'Leadership Team',
    'about.leadership.subtitle': 'Experienced professionals driving our success',
    'about.leadership.rajesh.name': 'Dr. Rajesh Kumar',
    'about.leadership.rajesh.position': 'CEO & Founder',
    'about.leadership.rajesh.background': 'Former pharmaceutical research director with 20+ years experience in API development and regulatory affairs across India and global markets.',
    'about.leadership.priya.name': 'Dr. Priya Sharma',
    'about.leadership.priya.position': 'Chief Technology Officer',
    'about.leadership.priya.background': 'PhD in Pharmaceutical Chemistry from IIT Mumbai, expert in process optimization and quality assurance systems with international certifications.',
    'about.leadership.amit.name': 'Amit Patel',
    'about.leadership.amit.position': 'VP of Global Sales',
    'about.leadership.amit.background': 'International business development leader with extensive experience in pharmaceutical supply chains and strategic partnerships across APAC and European markets.',
    'about.milestone.2009': 'Company founded in Pune, Maharashtra, India',
    'about.milestone.2012': 'ISO 9001:2015 certification achieved',
    'about.milestone.2015': 'First international API exports',
    'about.milestone.2018': 'FDA DMF submissions approved',
    'about.milestone.2020': 'Expanded to veterinary APIs',
    'about.milestone.2023': 'Serving 50+ countries globally',
    'about.values.title': 'Our Values',
    'about.values.subtitle': 'The principles that guide everything we do',
    'about.values.excellence.title': 'Excellence',
    'about.values.excellence.description': 'We pursue the highest standards in everything we do, from product quality to customer service, ensuring consistent excellence in all our operations.',
    'about.values.integrity.title': 'Integrity',
    'about.values.integrity.description': 'Transparency, honesty, and ethical business practices form the foundation of our relationships with customers, suppliers, and regulatory bodies.',
    'about.values.innovation.title': 'Innovation',
    'about.values.innovation.description': 'Continuous improvement and ongoing process research are how we keep developing better products and tighter manufacturing routes.',
    'about.cta.title': 'Ready to Partner with Us?',
    'about.cta.subtitle': 'Discover how Longshine Boi-Tech can support your pharmaceutical manufacturing needs with our premium APIs and exceptional service.',
    'about.cta.products': 'View Our Products',
    'about.cta.contact': 'Contact Our Team',

    // Contact Page
    'contact.hero.title': 'Contact Us',
    'contact.hero.description': 'Ready to partner with us? Our global team of pharmaceutical experts is here to support your API and intermediate supply requirements with personalized service and technical expertise.',
    'contact.hero.support': '24/7 Customer Support',
    'contact.hero.presence': 'Global Presence',
    'contact.form.title': 'Send Us a Message',
    'contact.form.additional': 'Additional Information',
    'contact.form.subtitle': 'Fill out the form below and our team will get back to you within 24 hours.',
    'contact.form.order_subtitle': 'Provide any additional requirements or special instructions for your order.',
    'contact.form.first_name': 'First Name',
    'contact.form.last_name': 'Last Name',
    'contact.form.email': 'Email Address',
    'contact.form.phone': 'Phone Number',
    'contact.form.company': 'Company Name',
    'contact.form.country': 'Country',
    'contact.form.inquiry_type': 'Inquiry Type',
    'contact.form.message': 'Message',
    'contact.form.files': 'Attach Files (Optional)',
    'contact.form.upload_text': 'Click to upload or drag and drop',
    'contact.form.upload_formats': 'Supported formats: PDF, DOC, XLS (Max 10MB)',
    'contact.form.consent': 'I agree to the processing of my personal data and consent to receive marketing communications from Longshine Boi-Tech. You can unsubscribe at any time.',
    'contact.form.send': 'Send Message',
    'contact.form.submit_additional': 'Submit Additional Information',
    'contact.form.placeholder.first_name': 'Enter your first name',
    'contact.form.placeholder.last_name': 'Enter your last name',
    'contact.form.placeholder.email': 'Enter your email',
    'contact.form.placeholder.phone': 'Enter your phone number',
    'contact.form.placeholder.company': 'Enter your company name',
    'contact.form.placeholder.country': 'Select your country',
    'contact.form.placeholder.message': 'Please provide details about your inquiry, including specific products of interest, quantities, and any technical requirements...',
    'contact.form.placeholder.message_order': 'Please provide any special requirements, delivery instructions, or additional information for your order...',
    'contact.quick.title': 'Quick Contact',
    'contact.quick.main_office': 'Main Office',
    'contact.quick.general': 'General Inquiries',
    'contact.quick.headquarters': 'Headquarters',
    'contact.response.title': 'Response Times',
    'contact.crypto.title': 'Crypto Payment Support',
    'contact.crypto.description': 'Need help with your cryptocurrency payment? Our crypto payment specialists are available 24/7.',
    'contact.crypto.support': 'Get Crypto Support',
    'contact.payment.complete_order': 'Complete Your Order',
    'contact.payment.review_items': 'Review your cart items and choose your preferred payment method. We accept major cryptocurrencies for fast, secure transactions.',
    'contact.payment.crypto': 'Cryptocurrency',
    'contact.payment.bank': 'Bank Transfer',
    'contact.payment.traditional': 'Traditional Payment Methods',
    'contact.payment.bank_desc': 'For bank transfers and traditional payment methods, please contact our sales team for detailed payment instructions.',
    'contact.payment.contact_sales': 'Contact Sales for Payment Details',

    // Products Page
    'products.hero.human_apis': 'Human APIs',
    'products.hero.intermediates': 'Pharmaceutical Intermediates',
    'products.hero.veterinary': 'Veterinary APIs',
    'products.hero.catalog': 'Product Catalog',
    'products.hero.human_desc': 'High-quality active pharmaceutical ingredients for human medicine, manufactured to international standards with comprehensive regulatory support.',
    'products.hero.intermediates_desc': 'Pharmaceutical intermediates and building blocks for drug synthesis — tight purity targets and a reliable supply track record.',
    'products.hero.veterinary_desc': 'Specialized active ingredients for veterinary applications, ensuring animal health and well-being with proven efficacy.',
    'products.hero.catalog_desc': 'Discover our comprehensive range of pharmaceutical active ingredients, intermediates, and veterinary compounds manufactured to international standards.',
    'products.hero.available': '+ Products Available',
    'products.search.placeholder': 'Search by CAS Number, Product Name, or Keyword...',
    'products.filter.by': 'Filter by:',
    'products.filter.category': 'Category',
    'products.filter.availability': 'Availability',
    'products.filter.advanced': 'Advanced Filters',
    'products.filter.export': 'Export Catalog',
    'products.showing': 'Showing {count} products',
    'products.results.all': 'All Products',
    'products.sort.relevance': 'Relevance',
    'products.sort.name': 'Name A-Z',
    'products.sort.category': 'Category',
    'products.sort.availability': 'Availability',

    // Footer
    'footer.company': 'Longshine Boi-Tech Co., Ltd.',
    'footer.certified': 'ISO 9001:2015 Certified Excellence',
    'footer.description': 'Leading pharmaceutical manufacturer delivering premium APIs, intermediates, and veterinary compounds to global markets with uncompromising quality.',
    'footer.products': 'Products',
    'footer.company_links': 'Company',
    'footer.human_apis': 'Human APIs',
    'footer.intermediates': 'Intermediates',
    'footer.veterinary': 'Veterinary APIs',
    'footer.all_products': 'All Products',
    'footer.about': 'About Us',
    'footer.capabilities': 'Capabilities',
    'footer.quality': 'Quality & Compliance',
    'footer.contact': 'Contact',
    'footer.copyright': '© 2026 Longshine Boi-Tech Pvt. Ltd. All rights reserved.',

    // Common
    'common.search': 'Search',
    'common.categories.all': 'All Categories',
    'common.categories.human': 'Human APIs',
    'common.categories.intermediates': 'Intermediates',
    'common.categories.veterinary': 'Veterinary APIs',
    'common.availability.all': 'All Availability',
    'common.availability.in_stock': 'In Stock',
    'common.availability.on_demand': 'On Demand',
    'common.availability.custom': 'Custom',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
};
